import { X } from "lucide-react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { useTapThroughGuard } from "@/lib/tapThroughGuard";
import { cn } from "@/lib/utils";
import "@/styles/morph-window.css";

import {
  closeButtonPlacement,
  measureTrigger,
  type CloseButtonPlacement,
  type ViewportBox,
} from "./closeButtonPlacement";
import { GLASS_ROUND_BUTTON } from "./MobileTopBar";
import {
  MORPH_CLOSE_MS,
  MORPH_OPEN_MS,
  REDUCED_FADE_MS,
  morphKeyframes,
  triggerOrigin,
} from "./morphGeometry";
import { trapTab } from "./trapTab";
import { usePageSheetDrag } from "./usePageSheetDrag";

/** Distance from the end under which the bottom fade is hidden. */
const END_THRESHOLD = 4;
const SPRING_OUT = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const BOUNCY = "cubic-bezier(0.22, 1.28, 0.36, 1)";

type Direction = "open" | "close";

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export interface MorphWindowProps {
  open: boolean;
  /** Dismiss request from the x, Escape or a swipe down. */
  onClose: () => void;
  /** Selector of the top-bar button the window grows from and returns to. */
  trigger: string;
  title: ReactNode;
  closeLabel: string;
  testId?: string;
  /** Scroll offset kept across a remount (locale change) while open. */
  keptScroll?: RefObject<number>;
  children: ReactNode;
}

/**
 * Full-screen liquid-glass window opened from a round top-bar button.
 *
 * It grows out of the tapped button: its clip-path is a spring-sampled
 * `inset(... round r)` running from the button's circle, through a soft
 * drop, to the whole physical screen (safe areas and the iOS vp-gap strip
 * included, see vp-gap.css), while the glass tint settles in and the content
 * zooms up from the button. Its close button sits exactly on the trigger
 * (the trigger fades under it), and closing runs the morph back into it.
 * Escape and a swipe down from the header (or from the top of the content)
 * close it too; Back is handled by the owner (useWindowHistory).
 *
 * Mounted already open (the app was remounted under it by a locale change),
 * it comes back in place, scrolled where it was, with no animation.
 */
export const MorphWindow = ({
  open,
  onClose,
  trigger,
  title,
  closeLabel,
  testId,
  keptScroll,
  children,
}: MorphWindowProps) => {
  const titleId = useId();
  const [remounted] = useState(open);
  const [present, setPresent] = useState(open);
  if (open && !present) setPresent(true);
  const [placement, setPlacement] = useState<CloseButtonPlacement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const windowRef = useRef<HTMLDivElement>(null);
  const tintRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const noBackdrop = useRef<HTMLElement>(null);
  const triggerBox = useRef<ViewportBox | null>(null);
  const skipIntro = useRef(remounted);
  const restoreTo = useRef<number | null>(null);
  const run = useRef<{ direction: Direction; animations: Animation[] } | null>(
    null,
  );
  useTapThroughGuard(open);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Puts the close button on the trigger before anything is painted.
  useLayoutEffect(() => {
    const element = windowRef.current;
    if (!present || !element) return;
    const measure = () => {
      const origin = element.getBoundingClientRect();
      const close = closeRef.current;
      // No trigger on screen (it should always be): grow from the x's own
      // CSS fallback box instead.
      const box = measureTrigger(trigger) ??
        (close && {
          left: close.offsetLeft + origin.left,
          top: close.offsetTop + origin.top,
          width: close.offsetWidth,
          height: close.offsetHeight,
        }) ?? { left: 16, top: 16, width: 40, height: 40 };
      triggerBox.current = box;
      setPlacement(closeButtonPlacement(box, origin.left, origin.top));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [present, trigger]);

  const measured = placement != null;

  useLayoutEffect(() => {
    if (!present || !measured) return;
    if (skipIntro.current) {
      skipIntro.current = false;
      if (open) return;
    }
    const direction: Direction = open ? "open" : "close";
    const current = run.current;
    // Interrupted mid-way: play the running motion back from where it is.
    if (
      current &&
      current.animations.some((animation) => animation.playState === "running")
    ) {
      if (current.direction !== direction) {
        current.direction = direction;
        current.animations.forEach((animation) => animation.reverse());
      }
      return;
    }
    current?.animations.forEach((animation) => animation.cancel());
    const animations = buildAnimations(direction, {
      window: windowRef.current,
      tint: tintRef.current,
      content: contentRef.current,
      close: closeRef.current,
      trigger: triggerBox.current,
    });
    const next = { direction, animations };
    run.current = next;
    const done = () => {
      if (run.current !== next) return;
      if (next.direction === "open") {
        // At rest the plain CSS is the open window: no clip left behind.
        next.animations.forEach((animation) => animation.cancel());
        run.current = null;
      } else {
        setPresent(false);
      }
    };
    if (animations.length === 0) {
      done();
      return;
    }
    Promise.all(animations.map((animation) => animation.finished)).then(
      done,
      // Cancelled by a newer run: that one finishes the job.
      () => {},
    );
  }, [open, present, measured]);

  useEffect(() => {
    if (present) return;
    run.current = null;
    setPlacement(null);
  }, [present]);

  // Focus moves into the window, Escape closes it.
  useEffect(() => {
    if (!open || !present) return;
    windowRef.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        onCloseRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, present]);

  // Focus goes back to the trigger when it closes (if it was in the window).
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      const active = document.activeElement;
      if (
        !active ||
        active === document.body ||
        windowRef.current?.contains(active)
      ) {
        document
          .querySelector<HTMLElement>(trigger)
          ?.focus({ preventScroll: true });
      }
    }
    wasOpen.current = open;
  }, [open, trigger]);

  // The remounted content may still be loading (too short to scroll that
  // far): re-applied as it grows, until reached or the user scrolls.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const content = scroller?.firstElementChild;
    if (!remounted || !scroller || !keptScroll) return;
    restoreTo.current ??= keptScroll.current;
    const target = restoreTo.current;
    if (!target) return;
    const restore = () => {
      scroller.scrollTop = target;
      return scroller.scrollTop >= target - 1;
    };
    if (restore() || !content) return;
    const observer = new ResizeObserver(() => {
      if (restore()) stop();
    });
    const stop = () => {
      observer.disconnect();
      scroller.removeEventListener("touchstart", stop);
      scroller.removeEventListener("wheel", stop);
    };
    observer.observe(content);
    scroller.addEventListener("touchstart", stop, { passive: true });
    scroller.addEventListener("wheel", stop, { passive: true });
    return stop;
  }, [remounted, keptScroll]);

  // Saved live when the window goes away (still attached in a layout
  // cleanup): the per-frame sample below can miss a scroll made just before
  // a remount.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!present || !scroller || !keptScroll) return;
    return () => {
      keptScroll.current = scroller.scrollTop;
    };
  }, [present, keptScroll]);

  // Scroll state for the progressive edges, sampled once per frame.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!present || !scroller) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      if (keptScroll) keptScroll.current = scrollTop;
      setScrolled(scrollTop > 0);
      setAtEnd(scrollTop + clientHeight >= scrollHeight - END_THRESHOLD);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [present, keptScroll]);

  const dragHandlers = usePageSheetDrag({
    sheetRef: windowRef,
    backdropRef: noBackdrop,
    scrollRef,
    enabled: open && present,
    onDismiss: () => onCloseRef.current(),
  });

  if (!present) return null;

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (windowRef.current) trapTab(event, windowRef.current);
  };

  return createPortal(
    <div
      ref={windowRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      data-testid={testId}
      data-state={open ? "open" : "closed"}
      data-scrolled={scrolled ? "true" : "false"}
      data-at-end={atEnd ? "true" : "false"}
      // Same layer as the tab bar and the popups (z-50): portalled after the
      // app it covers it, and the selects, tooltips and dialogs opened from
      // it are portalled after it, so they show above it instead of behind.
      className={cn("morph-window", !open && "pointer-events-none")}
      // Leaving: nothing in it can be tapped or focused, so a tap on the
      // trigger under the shrinking glass reopens it.
      inert={!open}
      onKeyDown={onKeyDown}
    >
      <div ref={tintRef} aria-hidden className="morph-window-tint" />
      <div
        ref={contentRef}
        className="morph-window-content"
        style={
          triggerBox.current
            ? { transformOrigin: triggerOrigin(triggerBox.current) }
            : undefined
        }
      >
        <div ref={scrollRef} className="morph-window-scroll">
          {children}
        </div>
        <div aria-hidden className="morph-window-edge morph-window-edge-top">
          <span className="morph-window-blur" />
          <span className="morph-window-blur" />
          <span className="morph-window-blur" />
          <span className="morph-window-edge-tint" />
        </div>
        <div aria-hidden className="morph-window-edge morph-window-edge-bottom">
          <span className="morph-window-edge-tint" />
        </div>
        <div
          data-testid={testId ? `${testId}-header` : undefined}
          className="morph-window-header"
          {...dragHandlers}
        >
          <h2 id={titleId} className="morph-window-title">
            {title}
          </h2>
        </div>
      </div>
      <button
        ref={closeRef}
        type="button"
        className={cn(GLASS_ROUND_BUTTON, "morph-window-close")}
        style={placement ?? undefined}
        onClick={() => onCloseRef.current()}
        aria-label={closeLabel}
      >
        <X className="size-5" strokeWidth={2} />
      </button>
    </div>,
    document.body,
  );
};

interface MorphParts {
  window: HTMLElement | null;
  tint: HTMLElement | null;
  content: HTMLElement | null;
  close: HTMLElement | null;
  trigger: ViewportBox | null;
}

/** The Web Animations for one open or close, all GPU-friendly properties. */
const buildAnimations = (
  direction: Direction,
  { window: element, tint, content, close, trigger }: MorphParts,
): Animation[] => {
  if (!element || typeof element.animate !== "function") return [];
  const opening = direction === "open";
  const fill: FillMode = "both";

  if (prefersReducedMotion() || !trigger) {
    return [
      element.animate(
        opening
          ? [{ opacity: 0 }, { opacity: 1 }]
          : [{ opacity: 1 }, { opacity: 0 }],
        { duration: REDUCED_FADE_MS, easing: "ease", fill },
      ),
    ];
  }

  const frame = { width: element.offsetWidth, height: element.offsetHeight };
  const clips = morphKeyframes(trigger, frame, direction).map((clipPath) => ({
    clipPath,
  }));
  const animations: Animation[] = [];

  if (opening) {
    animations.push(element.animate(clips, { duration: MORPH_OPEN_MS, fill }));
    // The glass settles from see-through into the window's surface.
    if (tint) {
      animations.push(
        tint.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 260,
          delay: 20,
          easing: "ease-out",
          fill,
        }),
      );
    }
    if (content) {
      animations.push(
        content.animate(
          [
            { opacity: 0, transform: "scale(0.9)" },
            { opacity: 1, transform: "none" },
          ],
          { duration: 340, delay: 70, easing: SPRING_OUT, fill },
        ),
      );
    }
    if (close) {
      animations.push(
        close.animate(
          [
            { opacity: 0, transform: "rotate(-90deg) scale(0.6)" },
            { opacity: 1, transform: "none" },
          ],
          { duration: 420, delay: 30, easing: BOUNCY, fill },
        ),
      );
    }
    return animations;
  }

  animations.push(element.animate(clips, { duration: MORPH_CLOSE_MS, fill }));
  // The last of the glass dissolves onto the trigger fading back in.
  animations.push(
    element.animate(
      [
        { opacity: 1, offset: 0 },
        { opacity: 1, offset: 0.72 },
        { opacity: 0, offset: 1 },
      ],
      { duration: MORPH_CLOSE_MS, easing: "ease-in", fill },
    ),
  );
  if (tint) {
    animations.push(
      tint.animate([{ opacity: 1 }, { opacity: 0.35 }], {
        duration: 240,
        delay: 60,
        easing: "ease-in",
        fill,
      }),
    );
  }
  if (content) {
    animations.push(
      content.animate(
        [
          { opacity: 1, transform: "none" },
          { opacity: 0, transform: "scale(0.9)" },
        ],
        { duration: 100, easing: "ease-out", fill },
      ),
    );
  }
  if (close) {
    animations.push(
      close.animate(
        [
          { opacity: 1, transform: "none" },
          { opacity: 0, transform: "scale(0.7)" },
        ],
        { duration: 140, delay: MORPH_CLOSE_MS - 150, easing: "ease-in", fill },
      ),
    );
  }
  return animations;
};
